import { useState } from '#imports'
import { computed, nextTick } from 'vue'

import type { Facet, UiPriceFacet } from '../types'

export function useExpandFilters() {
  const displayed = useState('plp-expand-filters-display', () => false)
  const type = useState<'filter' | 'sort'>(
    'plp-expand-filters-type',
    () => 'filter',
  )
  const selectedFacet = useState<Facet | UiPriceFacet | undefined>(
    'plp-expand-filters-selected-facet',
    () => undefined,
  )

  const triggerElement = useState<HTMLElement | null>()
  const modalElement = useState<HTMLElement | null>()
  const focusableElements = computed(
    () =>
      modalElement.value?.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      ) || [],
  )

  type Position = { left?: string; right?: string }
  const position = useState<Position>('plp-expand-filters-position', () => ({}))

  function close() {
    displayed.value = false
    selectedFacet.value = undefined
    position.value = {}

    // Move the focus on the trigger element after the modal is closed
    nextTick(() => {
      triggerElement.value?.focus()
    })
  }

  function filter(
    event: PointerEvent | KeyboardEvent,
    facet: Facet | UiPriceFacet,
    facetPosition: Position,
  ) {
    triggerElement.value = event.currentTarget as HTMLElement

    if (displayed.value && selectedFacet.value?.name === facet.name) close()
    else {
      type.value = 'filter'
      selectedFacet.value = facet
      position.value = facetPosition
      displayed.value = true

      focusFirstFocusableElement()
    }
  }

  function sort(event: PointerEvent | KeyboardEvent, facetPosition: Position) {
    triggerElement.value = event.currentTarget as HTMLElement
    if (displayed.value && type.value === 'sort') close()
    else {
      type.value = 'sort'
      selectedFacet.value = undefined
      position.value = facetPosition
      displayed.value = true

      focusFirstFocusableElement()
    }
  }

  function focusFirstFocusableElement() {
    nextTick(() => {
      focusableElements.value[0]?.focus()
    })
  }

  function focusTrap(event: KeyboardEvent) {
    const { key, shiftKey, target } = event

    if (key === 'Escape') {
      close()

      return
    }

    if (modalElement.value && key === 'Tab') {
      if (focusableElements.value.length < 2) {
        event.preventDefault()

        return
      }

      const last = focusableElements.value.length - 1

      if (shiftKey === false && target === focusableElements.value[last]) {
        event.preventDefault()
        focusableElements.value[0].focus()
      } else if (shiftKey === true && target === focusableElements.value[0]) {
        event.preventDefault()
        focusableElements.value[last].focus()
      }
    }
  }

  return {
    displayed,
    type,
    triggerElement,
    modalElement,
    filter,
    focusTrap,
    sort,
    close,
    selectedFacet,
    position,
  }
}
